import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  updateProfile
} from "firebase/auth";
import { auth } from "../Config/firebase";

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Add a state to track if logout is in progress
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Only update the current user if we're not in the middle of logging out
      if (!loggingOut) {
        setCurrentUser(user);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [loggingOut]);

  const signup = async (email, password, displayName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update user profile with display name
    if (displayName) {
      await updateProfile(userCredential.user, { displayName });
    }
    
    // Send verification email
    await sendEmailVerification(userCredential.user);
    
    // Immediately sign out the user after signup
    await signOut(auth);
    
    return userCredential;
  };
  
  // Update the auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!loggingOut) {
        // Only set as currentUser if email is verified
        setCurrentUser(user?.emailVerified ? user : null);
      }
      setLoading(false);
    });
  
    return unsubscribe;
  }, [loggingOut]);

  
  const login = async (email, password, rememberMe = false) => {
    await setPersistence(
      auth,
      rememberMe ? browserLocalPersistence : browserSessionPersistence
    );
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    if (!userCredential.user.emailVerified) {
      await signOut(auth);
      await sendEmailVerification(userCredential.user);
      throw new Error("Email not verified. Please verify your email before logging in.");
    }
    
    return userCredential;
  };

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const googleSignIn = async (rememberMe = false) => {
    const provider = new GoogleAuthProvider();
    
    // Set persistence based on remember me
    await setPersistence(
      auth,
      rememberMe ? browserLocalPersistence : browserSessionPersistence
    );
    
    return await signInWithPopup(auth, provider);
  };

  // Updated logout function to handle user state before actual Firebase signOut
  const logout = async () => {
    try {
      // Set logging out state to true before actual logout
      setLoggingOut(true);
      
      // Clear the current user state immediately to prevent UI flashes
      setCurrentUser(null);
      
      // Perform the actual Firebase sign out
      await signOut(auth);
      
      return true;
    } catch (error) {
      // Reset logging out state if there's an error
      setLoggingOut(false);
      throw error;
    } finally {
      // Reset the logging out state after a short delay to ensure navigation completes
      setTimeout(() => {
        setLoggingOut(false);
      }, 500);
    }
  };

  const resendVerificationEmail = async () => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }
    await sendEmailVerification(auth.currentUser);
  };

  const value = {
    currentUser,
    signup,
    login,
    googleSignIn,
    logout,
    resetPassword,
    resendVerificationEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}